import { Database, Mail, Phone, MessageSquare, Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { Customer } from '../../types';

interface PipelineCardLeadInfoProps {
  customer: Customer;
}

/**
 * Lead Information section for expanded pipeline cards
 * Displays source, submission date, contact preferences, and external data indicators
 */
export function PipelineCardLeadInfo({ customer }: PipelineCardLeadInfoProps) {
  const additionalData = customer.additionalData;

  // Determine the source display text
  const sourceText =
    additionalData?.external_database === true ||
    additionalData?.external_database === 'yes' ||
    customer.source === 'external landing page'
      ? 'Meta'
      : customer.source || 'Unknown';

  const hasContactPreferences =
    additionalData?.contactPreference || additionalData?.contact_preferences;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium flex items-center">
        <Database className="h-3.5 w-3.5 mr-1.5 text-primary" />
        Lead Information
      </div>
      <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-md text-xs space-y-2 shadow-sm">
        {/* Source with improved styling */}
        <div className="flex items-center">
          <span className="font-medium mr-1 text-slate-500 dark:text-slate-400">
            Source:
          </span>
          <span className="text-slate-700 dark:text-slate-300">{sourceText}</span>
        </div>

        {/* Submission date with improved styling */}
        {customer.createdAt && (
          <div className="flex items-center">
            <span className="font-medium mr-1 text-slate-500 dark:text-slate-400">
              Submitted:
            </span>
            <span className="text-slate-700 dark:text-slate-300">
              {new Date(customer.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </div>
        )}

        {/* Display contact preferences with enhanced styling */}
        {hasContactPreferences && (
          <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700/40">
            <div className="flex items-center mb-2">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400 mr-2">
                Preferred Contact:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* For structured contact preferences object with enhanced badge styling */}
              {additionalData?.contact_preferences?.email && (
                <div className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full text-xs shadow-sm">
                  <Mail className="h-3 w-3 mr-1" />
                  Email
                </div>
              )}
              {additionalData?.contact_preferences?.phone && (
                <div className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full text-xs shadow-sm">
                  <Phone className="h-3 w-3 mr-1" />
                  Phone
                </div>
              )}
              {additionalData?.contact_preferences?.text && (
                <div className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-full text-xs shadow-sm">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Text
                </div>
              )}

              {/* For simple string contactPreference with enhanced badge styling */}
              {additionalData?.contactPreference &&
                !additionalData?.contact_preferences && (
                  <div className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full text-xs shadow-sm">
                    <Bell className="h-3 w-3 mr-1" />
                    {additionalData.contactPreference}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Additional Data fields with improved styling */}
        {additionalData?.reference && (
          <div className="flex items-center mt-2">
            <span className="font-medium mr-1 text-slate-500 dark:text-slate-400">
              Reference:
            </span>
            <span className="text-slate-700 dark:text-slate-300">
              {additionalData.reference}
            </span>
          </div>
        )}

        {/* External data source indicator with improved badge styling */}
        {additionalData?.external_database && (
          <div className="mt-2 text-xs">
            <Badge
              variant="outline"
              className="bg-blue-50/50 text-blue-600 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60"
            >
              <Database className="h-3 w-3 mr-1" /> External Data
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}
